import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Package, Clock, CheckCircle2, AlertTriangle, Pause, XCircle, Truck, Loader2, Download, FileText, KeyRound, ScrollText, Lock, Unlock, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  STATUS_LABEL, STATUS_TONE, TONE_STYLE,
  deriveNextAction, displayFulfillmentType,
} from '@/lib/usbUxPresentation';

const API = process.env.REACT_APP_BACKEND_URL;

// v6.46 — Statuses where the drive artifact exists on R2 and a
// Super Admin should be one click away from downloading it.
const DOWNLOADABLE_STATUSES = ['qc_passed', 'packed', 'shipped', 'delivered'];

const StatusChip = ({ status }) => {
  const tone = STATUS_TONE[status] || 'neutral';
  const s = TONE_STYLE[tone];
  return (
    <span
      data-testid={`fulfill-status-chip-${status}`}
      style={{
        display: 'inline-block', padding: '2px 10px', borderRadius: 4,
        background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
        fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
      }}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
};

const ActionIcon = ({ action_id }) => {
  const common = { size: 18, style: { flexShrink: 0 } };
  if (action_id === 'prepare_usb')         return <Package {...common} />;
  if (action_id === 'preparing')           return <Loader2 className="animate-spin" {...common} />;
  if (action_id === 'ready_for_testing')   return <Clock {...common} />;
  if (action_id === 'package_and_ship')    return <CheckCircle2 {...common} />;
  if (action_id === 'confirm_delivery')    return <Truck {...common} />;
  if (action_id === 'needs_attention')     return <AlertTriangle {...common} />;
  if (action_id === 'try_again')           return <AlertTriangle {...common} />;
  if (action_id === 'resume')              return <Pause {...common} />;
  if (action_id === 'none')                return <XCircle {...common} />;
  return <Package {...common} />;
};

const priceLine = (order) => {
  if (order.fulfillment_type === 'demo') return 'FREE DEMO';
  const items = order.line_items || [];
  const total = items.reduce((acc, li) => acc + (li.unit_price || 0) * (li.quantity || 1), 0);
  if (!total) {
    // v6.46 — $0 customer order = free pilot / NFR grant / replacement etc.
    return order.fulfillment_type === 'customer' ? 'FREE PILOT' : '—';
  }
  return `$${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// v6.46 — Honest stick count. `line_items.quantity` is what the buyer
// paid for; `drives.length` is how many unique serials the pipeline
// minted. For a deterministic image flashed onto N identical sticks,
// quantity > drives.length is normal and intended (see
// order.physical_replication).
const stickCount = (order) => {
  const requested = (order.line_items || []).reduce((a, li) => a + (li.quantity || 1), 0);
  const minted = (order.drives || []).length;
  return { requested, minted };
};

const OrderCard = ({ order, onOpen, onDownload, onConfirmDelivery, onPinSlip, onPinSlipZip, onSoleVendorLetter, onCreateReplacement }) => {
  const action = deriveNextAction(order);
  const band = order.initial_grade_band || '—';
  const payOk = order.fulfillment_type === 'demo' ? true : Boolean(order.po_number || order.quote_number);
  const shipOk = Boolean(order?.shipping_address?.line1);
  const typeLabel = displayFulfillmentType(order);
  const { requested, minted } = stickCount(order);
  const sticks = Math.max(requested, minted, 1);
  const replication = order.physical_replication || null;
  const primarySerial = (order.drives || [])[0]?.serial || null;
  const canDownload = primarySerial && DOWNLOADABLE_STATUSES.includes(order.status);

  // v6.46 — Build a cardinality string that is honest about the
  // one-image-many-sticks case so ops never thinks they only owe 1
  // stick when the order was for 4. If a legacy order minted MORE
  // serials than were requested on the line_items, we still show
  // the real physical stick count (max of the two) and clarify the
  // over-minting in the parenthetical.
  const physicalSticks = Math.max(requested, minted, 1);
  let qtyLabel = `${physicalSticks} ${typeLabel} USB${physicalSticks === 1 ? '' : 's'}`;
  if (minted > 0 && requested > 0 && minted !== requested) {
    qtyLabel = `${physicalSticks} ${typeLabel} USB${physicalSticks === 1 ? '' : 's'} (${minted} unique serial${minted === 1 ? '' : 's'} · ${physicalSticks} physical stick${physicalSticks === 1 ? '' : 's'})`;
  }

  return (
    <div
      data-testid={`fulfill-order-card-${order.order_id}`}
      style={{
        border: '1px solid #E5E7EB', background: '#fff',
        padding: 16, marginBottom: 10, display: 'grid',
        gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center',
      }}
    >
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#0F1B2D', marginBottom: 2 }}>
          {order.school_name || order.district_name || '—'}
          <span style={{ color: '#6B7280', fontWeight: 400, marginLeft: 8, fontSize: 13 }}>
            {order.fulfillment_type === 'demo' ? '· Demo prospect' : (order.district_name || '')}
          </span>
        </div>
        <div style={{ fontSize: 13, color: '#374151', marginBottom: 6 }}>
          Grades {band} · {qtyLabel} · {priceLine(order)}
        </div>
        <div style={{ fontSize: 12, color: '#4B5563', marginBottom: 6 }}>
          Payment/PO {payOk ? '✓' : '✗'} · Shipping {shipOk ? '✓' : '✗'}
        </div>
        <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <StatusChip status={order.status} />
          {replication?.labels?.length ? (
            <span
              data-testid={`fulfill-replication-labels-${order.order_id}`}
              style={{
                fontSize: 11, color: '#4B5563',
                background: '#F3F4F6', border: '1px solid #E5E7EB',
                padding: '2px 8px', borderRadius: 4,
              }}
              title={`Hand-labeled from source serial ${replication.source_serial}`}
            >
              🏷️ {replication.labels.join(' · ')}
            </span>
          ) : null}
        </div>
        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
          {action.subtitle}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch' }}>
        <button
          onClick={() => {
            // v6.46 — Shipped orders: direct `/close` call bypasses the drawer so
            // one tap on the card actually transitions shipped → closed. Any other
            // status still opens the drawer for the full flow.
            if (action.action_id === 'confirm_delivery') {
              onConfirmDelivery(order);
            } else {
              onOpen(order, action);
            }
          }}
          disabled={action.disabled || action.action_id === 'none'}
          data-testid={`fulfill-next-action-${order.order_id}`}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 18px', fontSize: 14, fontWeight: 700,
            background: action.disabled ? '#E5E7EB' : '#002FA7',
            color: action.disabled ? '#6B7280' : '#fff',
            border: 'none', cursor: action.disabled ? 'not-allowed' : 'pointer',
            minWidth: 190, justifyContent: 'center',
          }}
        >
          <ActionIcon action_id={action.action_id} />
          {action.label || '—'}
        </button>
        {canDownload && (
          <button
            onClick={() => onDownload(order, primarySerial)}
            data-testid={`fulfill-download-${order.order_id}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', fontSize: 12, fontWeight: 600,
              background: '#fff', color: '#0F1B2D',
              border: '1px solid #0F1B2D', cursor: 'pointer',
              minWidth: 190, justifyContent: 'center',
            }}
            title={`Download the flashable image for ${primarySerial}`}
          >
            <Download size={14} /> Download image
          </button>
        )}
        <Link
          to={`/admin/usbs/${order.order_id}/manifest`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`fulfill-manifest-${order.order_id}`}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 14px', fontSize: 11, fontWeight: 600,
            background: 'transparent', color: '#4B5563',
            border: '1px solid #D1D5DB', cursor: 'pointer',
            textDecoration: 'none',
            minWidth: 190, justifyContent: 'center',
          }}
          title="Print a shipping / audit manifest for this order"
        >
          <FileText size={12} /> Print manifest
        </Link>
        {/* LERNO Sole-Vendor & Proprietary Publisher Certification letter.
            Available for every USB order (demo through closed) so Ops can
            attach a copy to any shipment or NYCPS procurement review. */}
        <button
          onClick={() => onSoleVendorLetter(order)}
          data-testid={`fulfill-sole-vendor-${order.order_id}`}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 14px', fontSize: 11, fontWeight: 600,
            background: 'transparent', color: '#002FA7',
            border: '1px solid #002FA7', cursor: 'pointer',
            minWidth: 190, justifyContent: 'center',
          }}
          title="Download LERNO Co. Sole-Vendor & Proprietary Publisher Certification letter (PDF) for this order."
        >
          <ScrollText size={12} /> Sole-Vendor Letter
        </button>
        {canDownload && (
          <button
            onClick={() => onPinSlip(order)}
            data-testid={`fulfill-pinslip-${order.order_id}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', fontSize: 11, fontWeight: 700,
              background: '#FFFBEB', color: '#92400E',
              border: '1px solid #F59E0B', cursor: 'pointer',
              minWidth: 190, justifyContent: 'center',
            }}
            title="Printable packing slip with the Drive Access PIN in a yellow callout — place in the shipping box"
          >
            <KeyRound size={12} /> Password Packing Slip
          </button>
        )}
        {canDownload && physicalSticks > 1 && (
          <button
            onClick={() => onPinSlipZip(order, physicalSticks)}
            data-testid={`fulfill-pinslip-zip-${order.order_id}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', fontSize: 11, fontWeight: 700,
              background: '#002FA7', color: '#fff',
              border: '1px solid #002FA7', cursor: 'pointer',
              minWidth: 190, justifyContent: 'center',
            }}
            title={`Download a ZIP of ${physicalSticks} numbered packing slips (one per stick, same password), ready to print and tuck into each box.`}
          >
            <KeyRound size={12} /> Print All Slips ({physicalSticks})
          </button>
        )}
        {/* UX1E (PS 149 recovery) — $0 replacement order factory.
            Shown on any shipped/closed non-replacement order so Ops can
            create a defective-shipment replacement without typing a
            parallel full-price order. */}
        {['shipped', 'closed', 'build_failed', 'qc_failed'].includes(order.status)
         && order.fulfillment_type !== 'replacement'
         && !order.archived && (
          <button
            onClick={() => onCreateReplacement && onCreateReplacement(order)}
            data-testid={`fulfill-create-replacement-${order.order_id}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', fontSize: 11, fontWeight: 700,
              background: '#FEF2F2', color: '#991B1B',
              border: '1px solid #DC2626', cursor: 'pointer',
              minWidth: 190, justifyContent: 'center',
            }}
            title="Create a $0 replacement order linked to this shipment. Reuses customer, license, and K-5 content. One new serial will be allocated at Authorize-time; the defective serial is preserved untouched."
          >
            <AlertTriangle size={12} /> Create Replacement
          </button>
        )}
      </div>
    </div>
  );
};

const FulfillOrdersBoard = ({ onOpenOrder }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [downloadingSerial, setDownloadingSerial] = useState(null);
  // Gate 3 governance strip — one-click open/close so mint dispatch
  // doesn't require a terminal curl.
  const [gate3, setGate3] = useState(null);
  const [gate3Err, setGate3Err] = useState(null);
  const [gate3Busy, setGate3Busy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const token = localStorage.getItem('mosaic_session_token');
      const res = await axios.get(`${API}/api/usb-orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Exclude terminal states from the Fulfill board; those live in History.
      const active = (res.data?.orders || []).filter(
        (o) => !['closed', 'rejected'].includes(o.status),
      );
      setOrders(active);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : (e.message || 'Could not load orders'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Gate 3 status loader — safe to poll; backend returns public shape.
  const loadGate3 = useCallback(async () => {
    try {
      const token = localStorage.getItem('mosaic_session_token');
      const res = await axios.get(`${API}/api/governance/gate3/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGate3(res.data);
      setGate3Err(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setGate3Err(typeof detail === 'string' ? detail : (e.message || 'Could not read Gate 3 status'));
    }
  }, []);

  useEffect(() => { loadGate3(); }, [loadGate3]);

  // One-click open — defaults to 2h TTL and the PS 149 replacement
  // scope so the operator doesn't have to type anything. Overridable
  // via two optional prompts.
  const handleGate3Open = async () => {
    const defaultScope = 'PS 149 Christa McAuliffe K-5 replacement mint — Owner authorized';
    const scope = (window.prompt(
      `Open Gate 3 — scope (why are you opening it)?\n` +
      `This is recorded on the audit log. Leave as-is if correct.`,
      defaultScope,
    ) || '').trim();
    if (!scope) return;
    if (scope.length < 10) {
      alert('Scope must be at least 10 characters.');
      return;
    }
    const ttlStr = (window.prompt(
      `TTL in hours (1-72). Gate 3 will auto-close after this.`,
      '2',
    ) || '').trim();
    const ttl = parseInt(ttlStr, 10);
    if (!ttl || ttl < 1 || ttl > 72) {
      alert('TTL must be a whole number between 1 and 72.');
      return;
    }
    setGate3Busy(true);
    try {
      const token = localStorage.getItem('mosaic_session_token');
      const { data } = await axios.post(
        `${API}/api/governance/gate3/open`,
        { scope, ttl_hours: ttl },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setGate3(data);
      alert(`Gate 3 OPEN until ${data.expires_at}\nScope: ${data.scope}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      alert(`Open Gate 3 failed: ${typeof detail === 'string' ? detail : (e.message || 'unknown error')}`);
    } finally {
      setGate3Busy(false);
    }
  };

  const handleGate3Close = async () => {
    const reason = (window.prompt(
      `Close Gate 3 — reason (recorded on audit log)?`,
      'manual close',
    ) || '').trim();
    if (!reason) return;
    const ok = window.confirm(
      `Close Gate 3 now?\n\nMint dispatch will be refused again until it is re-opened.`
    );
    if (!ok) return;
    setGate3Busy(true);
    try {
      const token = localStorage.getItem('mosaic_session_token');
      const { data } = await axios.post(
        `${API}/api/governance/gate3/close`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setGate3(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      alert(`Close Gate 3 failed: ${typeof detail === 'string' ? detail : (e.message || 'unknown error')}`);
    } finally {
      setGate3Busy(false);
    }
  };


  // v6.46 — One-click flashable-image download. We hit the signed-link
  // mint endpoint, then open the signed tarball URL in a new tab. The
  // signed URL is HMAC-based (no cookie needed), so a plain anchor
  // click lands the file in Downloads.
  const handleDownload = async (order, serial) => {
    if (!serial || downloadingSerial === serial) return;
    setDownloadingSerial(serial);
    try {
      const token = localStorage.getItem('mosaic_session_token');
      const { data } = await axios.get(`${API}/api/qc-usb/${serial}/mint-links`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = data?.links?.tarball;
      if (!url) throw new Error('Signed download link not returned');
      // Open in a new tab so the current page keeps its state.
      window.open(`${API}${url}`, '_blank', 'noopener');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      alert(`Download failed: ${typeof detail === 'string' ? detail : (e.message || 'unknown error')}`);
    } finally {
      setDownloadingSerial(null);
    }
  };

  // v6.46 — Password Packing Slip: axios blob fetch so the Authorization
  // header is sent (anchor-click won't carry it), then open the PDF in
  // a new tab via object URL. Super-admin-only on the backend; each
  // generation is audited and increments pin_reveal_count (field name
  // kept for backwards compat; display calls it "password" everywhere).
  const handlePinSlip = async (order) => {
    try {
      const token = localStorage.getItem('mosaic_session_token');
      const res = await axios.get(
        `${API}/api/usb-orders/${order.order_id}/packing-slip.pdf`,
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      // Give the new tab time to grab the blob before we revoke it.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      // Blob error bodies need async-text extraction.
      let detail = e?.message || 'unknown error';
      if (e?.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const parsed = JSON.parse(txt);
          detail = parsed.detail || txt;
        } catch { /* leave default */ }
      }
      alert(`Password packing slip failed: ${detail}`);
    }
  };

  // v6.48 — Batch Packing Slips (one-image-many-sticks). ZIP of N
  // numbered PDFs — one per stick, same password, "COPY X OF N" banner
  // + per-teacher label when available. One operator click replaces N
  // trips through the single-slip endpoint.
  const handlePinSlipZip = async (order, copyCount) => {
    try {
      const token = localStorage.getItem('mosaic_session_token');
      const res = await axios.get(
        `${API}/api/usb-orders/${order.order_id}/packing-slips.zip`,
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const url = URL.createObjectURL(res.data);
      // Trigger an actual file download (ZIP) rather than opening a tab.
      const a = document.createElement('a');
      a.href = url;
      a.download = `${order.order_id}_PackingSlips_x${copyCount}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      let detail = e?.message || 'unknown error';
      if (e?.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const parsed = JSON.parse(txt);
          detail = parsed.detail || txt;
        } catch { /* leave default */ }
      }
      alert(`Batch packing slips failed: ${detail}`);
    }
  };

  // UX1E — $0 Replacement Order factory. Prompts the operator so a
  // misclick can't silently fan out replacement orders; supports an
  // optional "supersedes_mint_attempt" (e.g. a stalled recovery
  // attempt whose artifact must be preserved) and a free-form audit
  // note. On success, reloads the board so the new order appears.
  const handleCreateReplacement = async (order) => {
    const ok = window.confirm(
      `Create a $0 replacement order for ${order.order_id}?\n\n` +
      `• Customer, shipping, license, and K-5 content will be cloned.\n` +
      `• No charge is applied; no duplicate license is issued.\n` +
      `• The defective shipment and any stalled attempt remain untouched.\n` +
      `• You will still need to Authorize the new order to allocate a serial and dispatch the mint.\n\n` +
      `Proceed?`
    );
    if (!ok) return;
    const supersedes = (window.prompt(
      `(Optional) Order ID of the stalled mint attempt this replacement supersedes.\n` +
      `Leave BLANK if there is no stalled attempt. Example: USB-DE5066D9FFDD`,
      ''
    ) || '').trim();
    const note = (window.prompt(
      `(Optional) Short audit note to record on the new order's timeline.\n` +
      `Example: PS 149 defective shipment replacement authorized by Owner.`,
      ''
    ) || '').trim();

    try {
      const token = localStorage.getItem('mosaic_session_token');
      const payload = {};
      if (supersedes) payload.supersedes_mint_attempt = supersedes;
      if (note) payload.note = note;
      const { data } = await axios.post(
        `${API}/api/usb-orders/${order.order_id}/replacement`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      alert(
        `Replacement created: ${data.order_id}\n` +
        `Status: ${data.status} · fulfillment_type: ${data.fulfillment_type}\n` +
        `Charge override: $${(data.amount_override_cents || 0) / 100}\n\n` +
        `Next step: open this order and click Authorize to allocate a serial and dispatch the mint.`
      );
      await load();
    } catch (e) {
      // 409 duplicates come back with structured detail; surface the existing id.
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.existing_order_id) {
        alert(
          `A live replacement already exists for ${order.order_id}:\n` +
          `${detail.existing_order_id} (status: ${detail.existing_status}).\n\n` +
          `Open that one instead of creating another.`
        );
      } else {
        alert(`Create replacement failed: ${typeof detail === 'string' ? detail : (e.message || 'unknown error')}`);
      }
    }
  };

  // Sole-Vendor Letter download — available for every USB order (any
  // status, including demo and replacement). SuperAdmin-only on the
  // backend; a timeline row is appended per download for audit.
  const handleSoleVendorLetter = async (order) => {
    try {
      const token = localStorage.getItem('mosaic_session_token');
      const res = await axios.get(
        `${API}/api/usb-orders/${order.order_id}/sole-vendor-letter.pdf`,
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      let detail = e?.message || 'unknown error';
      if (e?.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const parsed = JSON.parse(txt);
          detail = parsed.detail || txt;
        } catch { /* leave default */ }
      }
      alert(`Sole-vendor letter failed: ${detail}`);
    }
  };

  // v6.46 — Direct shipped → closed transition from the card. Mirrors
  // what the drawer's "Close Order (buyer confirmed receipt)" button
  // does but saves two clicks.
  const handleConfirmDelivery = async (order) => {
    const ok = window.confirm(
      `Confirm delivery of ${order.order_id}?\n\n` +
      `This marks the order COMPLETE (shipped → closed) and files it in USB History. ` +
      `Only click after the buyer has acknowledged receipt.`
    );
    if (!ok) return;
    try {
      const token = localStorage.getItem('mosaic_session_token');
      await axios.post(
        `${API}/api/usb-orders/${order.order_id}/close`,
        { reason: 'Buyer confirmed receipt' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      alert(`Confirm delivery failed: ${typeof detail === 'string' ? detail : (e.message || 'unknown error')}`);
    }
  };

  // Render helpers for the Gate 3 strip. Rendered on every board state
  // (loading / error / empty / populated) so the operator always sees
  // governance posture before touching an order.
  const renderGate3Strip = () => {
    const isOpen = !!(gate3 && gate3.open);
    const expired = !!(gate3 && gate3.expired);
    const expiresAt = gate3?.expires_at || null;
    let remainingLabel = '';
    if (isOpen && expiresAt) {
      try {
        const ms = new Date(expiresAt).getTime() - Date.now();
        if (ms > 0) {
          const mins = Math.floor(ms / 60000);
          const h = Math.floor(mins / 60);
          const m = mins % 60;
          remainingLabel = h > 0 ? `${h}h ${m}m` : `${m}m`;
        }
      } catch { /* ignore */ }
    }
    const bg = isOpen ? '#ECFDF5' : '#FEF2F2';
    const fg = isOpen ? '#065F46' : '#991B1B';
    const border = isOpen ? '#A7F3D0' : '#FECACA';
    return (
      <div
        data-testid="gate3-strip"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', marginBottom: 12, borderRadius: 6,
          background: bg, color: fg, border: `1px solid ${border}`,
          fontSize: 13,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isOpen ? <Unlock size={16} /> : <Lock size={16} />}
          <span style={{ fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase' }}>
            Gate 3 · {isOpen ? 'OPEN' : (expired ? 'EXPIRED' : 'CLOSED')}
          </span>
          <span data-testid="gate3-detail" style={{ color: fg, opacity: 0.9 }}>
            {gate3 == null && !gate3Err && 'checking…'}
            {gate3Err && `error: ${gate3Err}`}
            {isOpen && (
              <>
                {remainingLabel && <> · expires in <strong>{remainingLabel}</strong></>}
                {gate3.scope && <> · scope: <em>{gate3.scope}</em></>}
              </>
            )}
            {!isOpen && gate3 && !gate3Err && 'mint dispatch refused until re-opened'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isOpen ? (
            <button
              type="button"
              data-testid="gate3-close-btn"
              disabled={gate3Busy}
              onClick={handleGate3Close}
              style={{
                padding: '6px 12px', fontSize: 12, fontWeight: 700, borderRadius: 4,
                background: '#ffffff', color: '#991B1B', border: '1px solid #FECACA',
                cursor: gate3Busy ? 'not-allowed' : 'pointer',
              }}
            >
              {gate3Busy ? 'Working…' : 'Close Gate 3'}
            </button>
          ) : (
            <button
              type="button"
              data-testid="gate3-open-btn"
              disabled={gate3Busy}
              onClick={handleGate3Open}
              style={{
                padding: '6px 12px', fontSize: 12, fontWeight: 700, borderRadius: 4,
                background: '#065F46', color: '#ffffff', border: '1px solid #065F46',
                cursor: gate3Busy ? 'not-allowed' : 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <ShieldCheck size={14} />
              {gate3Busy ? 'Working…' : 'Open Gate 3 (2h)'}
            </button>
          )}
          <button
            type="button"
            data-testid="gate3-refresh-btn"
            onClick={loadGate3}
            disabled={gate3Busy}
            style={{
              padding: '6px 10px', fontSize: 12, fontWeight: 600, borderRadius: 4,
              background: '#ffffff', color: fg, border: `1px solid ${border}`,
              cursor: gate3Busy ? 'not-allowed' : 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div data-testid="fulfill-loading-wrap">
        {renderGate3Strip()}
        <div data-testid="fulfill-loading" style={{ padding: 24, color: '#6B7280' }}>Loading orders…</div>
      </div>
    );
  }
  if (err) {
    return (
      <div data-testid="fulfill-error-wrap">
        {renderGate3Strip()}
        <div data-testid="fulfill-error" style={{ padding: 24, color: '#991B1B' }}>Could not load orders: {err}</div>
      </div>
    );
  }
  if (!orders.length) {
    return (
      <div data-testid="fulfill-empty-wrap">
        {renderGate3Strip()}
        <div data-testid="fulfill-empty" style={{ padding: 24, color: '#6B7280', textAlign: 'center' }}>
          No USB orders need attention right now.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="fulfill-orders-board">
      {renderGate3Strip()}
      <div style={{ marginBottom: 10, fontSize: 12, color: '#6B7280' }}>
        {orders.length} active {orders.length === 1 ? 'order' : 'orders'}
      </div>
      {orders.map((o) => (
        <OrderCard
          key={o.order_id}
          order={o}
          onOpen={onOpenOrder}
          onDownload={handleDownload}
          onConfirmDelivery={handleConfirmDelivery}
          onPinSlip={handlePinSlip}
          onPinSlipZip={handlePinSlipZip}
          onSoleVendorLetter={handleSoleVendorLetter}
          onCreateReplacement={handleCreateReplacement}
        />
      ))}
    </div>
  );
};

export default FulfillOrdersBoard;
